import { useQuery } from '@tanstack/react-query'
import type { UseQueryOptions } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { getAdminOrgScope } from '#/shared/api/admin-org-scope'
import type { AuditTimelineResponse, DashboardActivity } from './models'
import { serviceClient } from '#/shared/api/http-client'

const ADMIN_BASE = '/audit/admin/api/v1/audit'

type QueryOpts<T> = { query?: Partial<UseQueryOptions<AxiosResponse<T>>> & { enabled?: boolean } }

export interface ResourceTimelineParams {
  limit?: number
  before?: string
}

function orgParams(extra?: Record<string, unknown>): Record<string, unknown> {
  const organization_id = getAdminOrgScope()
  return { ...(organization_id ? { organization_id } : {}), ...extra }
}

export const getResourceTimelineQueryKey = (
  rootResource?: string,
  rootId?: string,
  params?: ResourceTimelineParams,
) => [`/admin/api/v1/audit/${rootResource}/${rootId}/timeline`, ...(params ? [params] : [])] as const

/** Console route, not `/api/v1/audit/...`: that one filters on the caller's
 * own organization and rejects a console token. */
export function useResourceTimeline(
  rootResource: string,
  rootId: string,
  params?: ResourceTimelineParams,
  options?: QueryOpts<AuditTimelineResponse>,
) {
  return useQuery<AxiosResponse<AuditTimelineResponse>>({
    queryKey: getResourceTimelineQueryKey(rootResource, rootId, params),
    queryFn: () =>
      serviceClient.get(`${ADMIN_BASE}/${rootResource}/${rootId}/timeline`, {
        params: orgParams({ ...params }),
      }),
    ...options?.query,
  })
}

/**
 * The dashboard activity feed.
 *
 * Points at the console's own route, not `/api/v1/dashboard/activity`: that one
 * pins the query to the caller's `org_id` and rejects a console token outright,
 * so the Home view answered every load with a 401 and took the session with it.
 */
export function useActivity(params?: Record<string, unknown>, options?: {
  query?: Partial<import('@tanstack/react-query').UseQueryOptions<AxiosResponse<DashboardActivity>>> & { enabled?: boolean }
}) {
  return useQuery<AxiosResponse<DashboardActivity>>({
    queryKey: [`/admin/api/v1/audit/activity`, params],
    queryFn: () =>
      serviceClient.get(`${ADMIN_BASE}/activity`, { params: orgParams({ ...params }) }),
    ...options?.query,
  })
}
